import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import Unauthorised from "./Unauthorised";
import LoadingCover from "./LoadingCover";
import { useGetUsersMeQuery } from '../../redux/vmsApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { tokenStorage } from '../../redux/baseApi';

interface PrivateRouteProps {
  element?: React.ReactNode;
  roles?: string[];
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ element, roles }) => {
  const user = useAppSelector(state => state.auth.user);
  const location = useLocation();
  
  // Check for tokens in localStorage (primary) or cookies (fallback)
  // This ensures consistency with how API calls authenticate
  const localStorageToken = tokenStorage.getAccessToken();
  const cookieMatch = document.cookie.match('(^| )jwtToken=([^;]+)');
  const hasJwtToken = (localStorageToken && localStorageToken.length > 10) || 
                      (cookieMatch && cookieMatch[2] && cookieMatch[2].length > 10);
  
  // Use the getUsersMe query to check auth status - only run if we have a token but no user
  const { isLoading, isError } = useGetUsersMeQuery(
    hasJwtToken && !user ? undefined : skipToken
  );

  // Still loading auth check
  if (hasJwtToken && !user && isLoading) {
    return <LoadingCover />;
  }

  // Token exists but auth failed (expired/invalid token) - or no token at all
  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (roles && roles.length > 0 && !roles.some(role => user.roles && user.roles.includes(role))) {
    return <Unauthorised />;
  }

  return element ? <>{element}</> : <Outlet />;
};

export default PrivateRoute;
